import { Button, Descriptions, Flex, Image, Input, message, Popconfirm, Popover, Select, Space, Table } from 'antd';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { CheckCircleOutlined, CheckSquareOutlined, CloseOutlined, CommentOutlined, PlusOutlined, SearchOutlined, SnippetsOutlined, StopOutlined } from '@ant-design/icons';
import EditModel from './EditModel';
import ApproveModel from './ApproveModel';
import { useDebounce } from 'use-debounce';
import { Filter, FilterGroup } from '../../types/types';
import { GenerateODataFilter } from '../../helpers/odataFunctions';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import { AssetApprovalStatus, AssetApprovalStatusLabels, AssetApprovalStatusOptions } from '../../models/enums';
import { UserSelector } from '../Inputs/UserSelector';
import { AuthContext } from '../../contexts/AuthContext';
import { MetadataSelector } from '../Inputs/MetadataSelector';
import { useModelServiceDeleteBackendv2Model, useModelServiceGetBackendv2Model, useModelServiceGetBackendv2ModelKey, useModelServicePutBackendv2Model, useUiMetaDataServiceGetBackendv2UimetadataBackendlist, useUiMetaDataServiceGetBackendv2UimetadataBackendlistKey } from '../../backendapi/queries';
import { ModelViewDTO } from '../../backendapi/requests';
import ReactTimeago from 'react-timeago';
import { ConfigContext } from '../../contexts/ConfigContext';
import MetadataTag from '../layout/elements/MetadataTag';
import { BASE_PATH } from '../../Environment';

//const metadateKeys: string[] = ['ean', 'brand', 'banner'];

const ModelList: React.FC = () => {

    const { userid } = useContext(AuthContext);
    const { currentClient } = useContext(ConfigContext);

    const [messageApi, contextHolder] = message.useMessage();
    const [selectedModel, setSelectedModel] = useState<ModelViewDTO | undefined>(undefined);

    const [approveOpen, setApproveOpen] = useState(false);

    const [query, setQuery] = useState("");
    const [debouncedQuery] = useDebounce(query, 500);
    const [selectedApprovalStatuses, setSelectedApprovalStatuses] = useState<number[]>([]);
    const [selectedStatuses, setSelectedStatuses] = useState<number[]>([]);
    const [selectedWhitemodel, setSelectedWhitemodel] = useState<number[]>([]);
    //const [selectedClients, setSelectedClients] = useState<number[]>([]);
    const [selectedUser, setSelectedUser] = useState<number | undefined>(undefined);
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(1);
    const [sortField, setSortField] = useState<string>("id");
    const [sortOrder, setSortOrder] = useState<"desc" | "asc">("desc");
    const [customFilters, setCustomFilters] = useState<Filter[]>([]);

    const { data: uiMetaData, isFetched } = useUiMetaDataServiceGetBackendv2UimetadataBackendlist({ clientId: currentClient.id }, [useUiMetaDataServiceGetBackendv2UimetadataBackendlistKey, currentClient.id], { refetchOnWindowFocus: false, enabled: currentClient.id > 0 })
    const metadataKeys = useMemo(() => {
        return uiMetaData?.map(x => x.fieldName) ?? []
    }, [currentClient, isFetched]);

    const filters = useMemo(() => {
        let filters: (Filter | FilterGroup)[] = [];

        if (debouncedQuery) {
            filters.push({
                type: 'group', filters: [
                    { type: 'search', values: [{ value: debouncedQuery }], property: 'title', name: 'title' },
                    { type: 'search', values: [{ value: debouncedQuery }], property: uiMetaData?.map(e => 'metadata.' + e.fieldName).join(',') ?? '', name: 'metadata' }
                ], join: 'or'
            });
        }

        if (selectedApprovalStatuses.length > 0) {
            filters.push({ type: 'select', values: selectedApprovalStatuses.map(e => ({ value: e })), property: 'approvalStatus', name: 'approvalStatus' });
        }

        if (selectedStatuses.length > 0) {
            filters.push({ type: 'select', values: selectedStatuses.map(e => e === 1).map(e => ({ value: e })), property: 'isEnabled', name: 'isEnabled' });
        }

        if (selectedWhitemodel.length > 0) {
            filters.push({ type: 'select', values: selectedWhitemodel.map(e => e === 1).map(e => ({ value: e })), property: 'isWhitemodel', name: 'isWhitemodel' });
        }

        if (selectedUser) {
            filters.push({ type: 'select', values: [{ value: selectedUser }], property: 'producingArtistId', name: 'producingArtistId' });
        }

        for (let i = 0; i < customFilters.length; i++) {
            if (customFilters[i].values.length > 0) {
                filters.push(customFilters[i]);
            }
        }

        return filters;
    }, [debouncedQuery, selectedApprovalStatuses, selectedStatuses, currentClient, selectedUser, customFilters, selectedWhitemodel, uiMetaData]);

    useEffect(() => {
        setPage(1);
    }, [currentClient, debouncedQuery, selectedApprovalStatuses, selectedStatuses, customFilters]);

    const { data, refetch, isFetching } = useModelServiceGetBackendv2Model({
        clientId: currentClient.id,
        top: pageSize.toString(),
        skip: ((page - 1) * pageSize).toString(),
        filter: GenerateODataFilter(filters),
        orderby: `${sortField} ${sortOrder}`,
        count: 'true'
    }, undefined, { refetchOnWindowFocus: false, placeholderData: (prev) => prev, enabled: isFetched });

    const { mutateAsync: deleteModel } = useModelServiceDeleteBackendv2Model();
    const { mutateAsync: editModel } = useModelServicePutBackendv2Model();

    const handleCustomFilterAdd = useCallback(() => {
        setCustomFilters(v => [...v, { type: 'select', values: [], property: '', name: '' }]);
    }, []);
    const handleCustomFilterRemove = useCallback((index: number) => {
        setCustomFilters(v => v.filter((e, i) => i !== index));
    }, []);
    const handleCustomFilterChange = useCallback((value: Filter, index: number) => {
        setCustomFilters(v => v.map((e, i) => i === index ? value : e));
    }, []);

    const handleApprovalStatusChange = useCallback((value: any, option?: { label: string; value: string; } | { label: string; value: string; }[]) => {
        if (Array.isArray(option)) {
            setSelectedApprovalStatuses(option.map(e => parseInt(e.value)));
        }
    }, []);

    const handleStatusChange = useCallback((value: any, option?: { label: string; value: string; } | { label: string; value: string; }[]) => {
        if (Array.isArray(option)) {
            setSelectedStatuses(option.map(e => parseInt(e.value)));
        }
    }, []);

    const handleWhitemodelChange = useCallback((value: any, option?: { label: string; value: string; } | { label: string; value: string; }[]) => {
        if (Array.isArray(option)) {
            setSelectedWhitemodel(option.map(e => parseInt(e.value)));
        }
    }, []);

    /*const handleClientChange = useCallback((value: any, option?: { label: string; value: string; } | { label: string; value: string; }[]) => {
        if (Array.isArray(option)) {
            setSelectedClients(option.map(e => parseInt(e.value)));
        }
    }, []);*/

    const handleUserChange = useCallback((value?: number) => {
        setSelectedUser(value);
    }, []);

    const handleEditClose = useCallback(() => {
        setSelectedModel(undefined);
        refetch();
    }, [refetch]);

    const startApproval = () => {
        setApproveOpen(true);
    };

    const handleApprovalClose = useCallback(() => {
        setApproveOpen(false);
        refetch();
    }, [refetch]);

    const handleTableChange = useCallback((pagination: TablePaginationConfig, filters: Record<string, FilterValue | null>, sorter: SorterResult<ModelViewDTO> | SorterResult<ModelViewDTO>[]) => {
        setPage(pagination.current ?? 1);
        setPageSize(pagination.pageSize ?? 10);

        if (!Array.isArray(sorter)) {
            setSortField(sorter.field?.toString() ?? "id");
            setSortOrder(sorter.order === 'ascend' ? 'asc' : 'desc');
        }
    }, []);

    const handleDelete = useCallback((model: ModelViewDTO) => {
        if (model.id) {
            deleteModel({ key: model.id }).then(() => {
                messageApi.success("Model deleted");
                refetch();
            });
        }
    }, [deleteModel, messageApi, refetch]);

    const handleEnable = useCallback((model: ModelViewDTO) => {
        if (model.id) {
            editModel({ key: model.id, requestBody: { ...model, isEnabled: true } }).then(() => {
                messageApi.success("Model enabled");
                refetch();
            })
        }
    }, [editModel, messageApi, refetch]);

    const handleDisable = useCallback((model: ModelViewDTO) => {
        if (model.id) {
            editModel({ key: model.id, requestBody: { ...model, isEnabled: false } }).then(() => {
                messageApi.success("Model disabled");
                refetch();
            })
        }
    }, [editModel, messageApi, refetch]);

    const showMyTasks = useCallback(() => {
        setSelectedUser(userid);
        setSelectedApprovalStatuses([AssetApprovalStatus.Pending, AssetApprovalStatus.InProduction, AssetApprovalStatus.Rejected]);
    }, [userid]);

    const columns: ColumnsType<ModelViewDTO> = [
        {
            title: 'Id',
            dataIndex: 'id',
            key: 'id',
            sorter: (a, b) => (a.id || 0) - (b.id || 0),
        },
        {
            title: 'Thumbnail',
            dataIndex: 'thumbnail',
            key: 'title',
            render: (_, value) => (
                <Image
                    src={BASE_PATH + value.thumbnail}
                    preview={{ src: BASE_PATH + value.image }}
                    height={100}
                />)
        },
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
            sorter: (a, b) => (a.title || '').localeCompare(b.title || ''),
        },
        {
            title: 'Enabled',
            dataIndex: 'enabled',
            key: 'enabled',
            render: (_, value) => (value.isEnabled ? <CheckCircleOutlined style={{ color: '#389e0d' }} /> : <StopOutlined style={{ color: '#cf1322' }} />)
        },
        {
            title: 'Member of',
            dataIndex: 'modelpacks',
            key: 'modelpacks',
            render: (_, value) => value.modelpacks?.map(e => e.name).join(", ")
        },
        {
            title: 'Status',
            dataIndex: 'approvalStatus',
            key: 'approvalStatus',
            render: (_, value) => (AssetApprovalStatusOptions[value.approvalStatus as AssetApprovalStatus])
        },
        {
            title: 'Data import',
            dataIndex: 'timestampMetadataImport',
            key: 'timestampMetadataImport',
            render: (_, value) => (value.timestampMetadataImport ? <ReactTimeago date={value.timestampMetadataImport} /> : null),
        },
        {
            title: 'Metadata',
            dataIndex: 'metadata',
            key: 'metadata',
            width: 200,
            render: (_, { metadata }) => (metadata?.filter(e => metadataKeys.includes(e.name.toLowerCase())).map(e => (<MetadataTag key={e.name + e.value} name={e.name.toLowerCase()} label={e.value.toLowerCase()} />)))
        },
        {
            title: 'Comments',
            dataIndex: 'comments',
            key: 'comments',
            width: 50,
            render: (_, { approvalHistory }) => {
                if (approvalHistory) {
                    const item = approvalHistory.findLast(e => e.comment);

                    if (item?.comment) {
                        return (<Popover content={item.comment} title={<>{item.username} <ReactTimeago date={item.timestamp} /></>}>
                            <Button><CommentOutlined /></Button>
                        </Popover>)
                    }
                }
                return null;
            },
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Space size="middle">
                    <Button type="link" onClick={() => setSelectedModel(record)}>Edit</Button>
                    {record.isEnabled ?
                        <Button type="link" onClick={() => handleDisable(record)}>Disable</Button> :
                        <Button type="link" onClick={() => handleEnable(record)}>Enable</Button>
                    }
                    <Popconfirm title="Sure to delete?" onConfirm={() => handleDelete(record)}>
                        <Button type="link">Delete</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <>
            {contextHolder}

            <Flex justify='space-between' style={{ marginBottom: 16 }}>

                <Space>
                    <Input prefix={<SearchOutlined />} placeholder='Search' value={query} onChange={e => setQuery(e.target.value)} />
                    {/* <Select
                        mode="multiple"
                        placeholder="Client"
                        onChange={handleClientChange}
                        popupMatchSelectWidth={false}
                        size='middle'
                        style={{ minWidth: 100 }}
                        options={clients?.value.map(e => ({ label: e.name, value: e.id.toString() }))}
                    /> */}
                    <Select
                        mode="multiple"
                        placeholder="Enabled"
                        onChange={handleStatusChange}
                        popupMatchSelectWidth={false}
                        size='middle'
                        style={{ minWidth: 100 }}
                        options={[
                            {
                                label: 'True',
                                value: '1'
                            },
                            {
                                label: 'False',
                                value: '0'
                            }
                        ]}
                    />
                    <Select
                        mode="multiple"
                        placeholder="Whitemodel"
                        onChange={handleWhitemodelChange}
                        popupMatchSelectWidth={false}
                        size='middle'
                        style={{ minWidth: 150 }}
                        options={[
                            {
                                label: 'True',
                                value: '1'
                            },
                            {
                                label: 'False',
                                value: '0'
                            }
                        ]}
                    />
                    <Select
                        mode="multiple"
                        placeholder="Status"
                        onChange={handleApprovalStatusChange}
                        popupMatchSelectWidth={false}
                        value={selectedApprovalStatuses.map(e => e.toString())}
                        size='middle'
                        style={{ minWidth: 100 }}
                        options={[
                            {
                                label: AssetApprovalStatusLabels[AssetApprovalStatus.Quote],
                                value: AssetApprovalStatus.Quote.toString()
                            },
                            {
                                label: AssetApprovalStatusLabels[AssetApprovalStatus.Pending],
                                value: AssetApprovalStatus.Pending.toString()
                            },
                            {
                                label: AssetApprovalStatusLabels[AssetApprovalStatus.InProduction],
                                value: AssetApprovalStatus.InProduction.toString()
                            },
                            {
                                label: AssetApprovalStatusLabels[AssetApprovalStatus.Commited],
                                value: AssetApprovalStatus.Commited.toString()
                            },
                            {
                                label: AssetApprovalStatusLabels[AssetApprovalStatus.New],
                                value: AssetApprovalStatus.New.toString()
                            },
                            {
                                label: AssetApprovalStatusLabels[AssetApprovalStatus.Rejected],
                                value: AssetApprovalStatus.Rejected.toString()
                            },
                            {
                                label: AssetApprovalStatusLabels[AssetApprovalStatus.RejectedPendingModification],
                                value: AssetApprovalStatus.RejectedPendingModification.toString()
                            },
                            {
                                label: AssetApprovalStatusLabels[AssetApprovalStatus.RejectedButModified],
                                value: AssetApprovalStatus.RejectedButModified.toString()
                            },
                            {
                                label: AssetApprovalStatusLabels[AssetApprovalStatus.Approved],
                                value: AssetApprovalStatus.Approved.toString()
                            },
                            {
                                label: AssetApprovalStatusLabels[AssetApprovalStatus.ApprovedPendingModification],
                                value: AssetApprovalStatus.ApprovedPendingModification.toString()
                            },
                            {
                                label: AssetApprovalStatusLabels[AssetApprovalStatus.ApprovedButModified],
                                value: AssetApprovalStatus.ApprovedButModified.toString()
                            }
                        ]}
                    />
                    <UserSelector placeholder="Assigned to" value={selectedUser} onChange={handleUserChange} style={{ width: 250 }} />
                </Space>

                <Space>
                    <Button type="primary" onClick={showMyTasks} icon={<SnippetsOutlined />}>
                        Show my tasks
                    </Button>
                    <Button type="primary" onClick={startApproval} icon={<CheckSquareOutlined />}>
                        Start approving
                    </Button>
                </Space>
            </Flex>

            <Space orientation='vertical' style={{ marginBottom: 16 }}>
                {customFilters.map((e, i) => <Space key={e.property}><MetadataSelector assetType='model' value={e} onChange={v => handleCustomFilterChange(v, i)} /><Button onClick={() => handleCustomFilterRemove(i)} icon={<CloseOutlined />}></Button></Space>)}
                <Button onClick={handleCustomFilterAdd} icon={<PlusOutlined />}>Filter</Button>
            </Space>

            <EditModel isOpen={selectedModel != null} onClose={handleEditClose} modelId={selectedModel?.id} />

            <Table
                loading={isFetching}
                dataSource={data?.value}
                columns={columns}
                rowKey={"id"}
                onChange={handleTableChange}
                showSorterTooltip={false}
                pagination={{
                    total: (data !== undefined && data['@odata.count']) ? data['@odata.count'] : 0,
                    current: page,
                    pageSize: pageSize,
                    pageSizeOptions: [10, 20, 50, 100],
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                    showQuickJumper: true,
                    showSizeChanger: true,
                }} />

            <ApproveModel isOpen={approveOpen} models={data?.value ?? []} onClose={handleApprovalClose} />
        </>
    );
};

export default ModelList;